import React, { useState } from 'react';
import { useQuizContext } from '../context/QuizContext';

const Welcome = () => {
  const { setCurrentStep } = useQuizContext();
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [isAdult, setIsAdult] = useState(false);

  // Colores que armonizan con el fondo
  const panelBgColor = '#2a3c67'; // Azul marino oscuro, coincide con las hojas del fondo
  const buttonColor = '#c84357'; // Rojo similar al de los elementos del logo y fondo
  const colorLogo = '#030645'; // Azul oscuro para el panel
  
  // Función para generar URL del QR code
  const qrCodeUrl = "https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://tienda.commerk.com";
  
  // Textos formateados para los modales con títulos destacados
  const termsText = `**Términos y Condiciones de Uso – App de Recomendación de Licores**

Esta aplicación es desarrollada y operada por **Commerk S.A.S.**, empresa distribuidora autorizada de productos de la **Industria Licorera de Caldas**. Su finalidad es brindar recomendaciones de licores utilizando inteligencia artificial, con base en el portafolio disponible de nuestra empresa.

Las sugerencias generadas por la aplicación son de carácter orientativo y no constituyen asesoría profesional. Pueden no considerar factores personales, de salud o contextuales del usuario, y podrían contener imprecisiones o errores ocasionales. **Commerk S.A.S.** no garantiza la precisión absoluta de las recomendaciones ni se hace responsable por las decisiones de consumo basadas en ellas.

El uso de esta app es voluntario y bajo responsabilidad exclusiva del usuario. Al utilizarla, el usuario acepta expresamente estas condiciones.

**Consuma con moderación. Prohibido el expendio de bebidas embriagantes a menores de edad.**

**Derechos de Uso y Propiedad Intelectual**

Todo el contenido de esta aplicación—incluidos pero no limitado a textos, descripciones de productos, logotipos, imágenes, interfaz de usuario y recomendaciones generadas por inteligencia artificial de uso comercial.

Está prohibida la reproducción, distribución, modificación o uso del contenido para fines comerciales o no autorizados, sin el consentimiento expreso y por escrito de **Commerk S.A.S.** o del respectivo titular de los derechos.

El modelo de inteligencia artificial incorporado en esta app es una herramienta de apoyo interactiva y no sustituye el juicio humano ni el consumo informado y responsable.`;
  
  // URL externa para la política de tratamiento de datos (la conservamos para el componente Registration)
  const privacyPolicyUrl = "https://tiendacommerkvalle.com.co/wp-content/uploads/2025/02/politicas-tratamiento-datos.pdf";
  
  return (
    <div 
      className="flex flex-col items-center justify-center min-h-screen p-4"
      style={{
        backgroundImage: 'url(/assets/images/background.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div 
        className="max-w-md w-full rounded-xl shadow-lg bg-opacity-90"
        style={{ backgroundColor: colorLogo }}
      >
        {/* Logo dentro del panel azul con tamaño grande - Reducido el espaciado superior */}
        <div className="flex justify-center mt-2">
          <img 
            src="/assets/logos/logo3.jpeg" 
            alt="COMMERK s.a.s." 
            className="h-40"
          />
        </div>
        
        {/* Contenido centrado en el panel - Reducido el espaciado superior del texto */}
        <div className="px-8 pb-6 pt-0 text-center">
          <h1 className="text-4xl font-bold mb-2 text-white">
            ¡Descubre tu licor ideal!
          </h1>
          
          <p className="text-lg mb-3 text-white leading-relaxed">
            Participa en nuestra experiencia interactiva y descubre qué licor de nuestra exclusiva 
            selección combina perfectamente con tu personalidad.
          </p>
          
          {/* Sección QR con menos espacio */}
          <div className="mb-3 flex flex-col items-center">
            <p className="text-white mb-1">Escanea para acceder a nuestra app:</p>
            <img 
              src={qrCodeUrl} 
              alt="QR Code para acceder a la aplicación" 
              className="w-32 h-32 bg-white p-1 rounded"
            />
          </div>
          
          {/* Checkbox para verificar mayoría de edad - más compacto */}
          <div className="mb-3 flex items-center justify-center">
            <input 
              type="checkbox" 
              id="adultCheck" 
              checked={isAdult} 
              onChange={() => setIsAdult(!isAdult)} 
              className="mr-2 h-5 w-5"
            />
            <label htmlFor="adultCheck" className="text-white">
              Declaro que soy mayor de edad
            </label>
          </div>
          
          <button
            onClick={() => setCurrentStep(1)}
            disabled={!isAdult}
            className={`text-white font-bold py-3 px-8 rounded-full shadow-md transition-all duration-300 ${isAdult ? 'hover:shadow-lg' : 'opacity-50 cursor-not-allowed'}`}
            style={{ backgroundColor: buttonColor }}
          >
            ¡Comenzar Ahora!
          </button>
          
          {/* Texto reducido y enlaces para los modales más cerca del botón */}
          <p className="mt-2 text-sm text-white opacity-80">
            Sólo te tomará un minuto y podrás obtener una recomendación personalizada.
          </p>
          
          {/* Solo botón de Términos y Condiciones del mismo color que el botón principal */}
          <div className="mt-2 flex justify-center">
            <button 
              onClick={() => setShowTermsModal(true)} 
              className="text-white underline text-sm hover:text-gray-300"
              style={{ color: "#eae7e7" }}
            >
              Términos y Condiciones
            </button>
          </div>
        </div>
      </div>
      
      {/* Modal de Términos y Condiciones con formato mejorado */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-screen overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Términos y Condiciones</h2>
                <button 
                  onClick={() => setShowTermsModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
              <div className="terms-content">
                <h3 className="text-lg font-bold text-center mb-4 bg-blue-100 py-2 px-4 rounded-md">Términos y Condiciones de Uso – App de Recomendación de Licores</h3>
                
                <p className="mb-4">
                  Esta aplicación es desarrollada y operada por <strong>Commerk S.A.S.</strong>, empresa distribuidora
                  autorizada de productos de la <strong>Industria Licorera de Caldas</strong>. Su finalidad es brindar
                  recomendaciones de licores utilizando inteligencia artificial, con base en el portafolio
                  disponible de nuestra empresa.
                </p>
                
                <p className="mb-4">
                  Las sugerencias generadas por la aplicación son de carácter orientativo y no constituyen
                  asesoría profesional. Pueden no considerar factores personales, de salud o contextuales del
                  usuario, y podrían contener imprecisiones o errores ocasionales. <strong>Commerk S.A.S.</strong> no
                  garantiza la precisión absoluta de las recomendaciones ni se hace responsable por las
                  decisiones de consumo basadas en ellas.
                </p>
                
                <p className="mb-4">
                  El uso de esta app es voluntario y bajo responsabilidad exclusiva del usuario. Al utilizarla, el
                  usuario acepta expresamente estas condiciones.
                </p>
                
                <p className="mb-6 font-bold bg-yellow-100 py-2 px-4 rounded-md text-center">
                  Consuma con moderación. Prohibido el expendio de bebidas embriagantes a
                  menores de edad.
                </p>
                
                <h3 className="text-lg font-bold mb-4 bg-blue-100 py-2 px-4 rounded-md">Derechos de Uso y Propiedad Intelectual</h3>
                
                <p className="mb-4">
                  Todo el contenido de esta aplicación—incluidos pero no limitado a textos, descripciones de
                  productos, logotipos, imágenes, interfaz de usuario y recomendaciones generadas por
                  inteligencia artificial de uso comercial.
                </p>
                
                <p className="mb-4">
                  Está prohibida la reproducción, distribución, modificación o uso del contenido para fines
                  comerciales o no autorizados, sin el consentimiento expreso y por escrito de <strong>Commerk
                  S.A.S.</strong> o del respectivo titular de los derechos.
                </p>
                
                <p className="mb-4">
                  El modelo de inteligencia artificial incorporado en esta app es una herramienta de apoyo
                  interactiva y no sustituye el juicio humano ni el consumo informado y responsable.
                </p>
              </div>
              <div className="mt-6 flex justify-end">
                <button 
                  onClick={() => setShowTermsModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Welcome;